import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import Header from "@/components/Header";

const LAST_UPDATED = "January 2026";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen w-full flex flex-col text-zinc-900">
      <Header />
      <section className="max-w-3xl mx-auto w-full px-6 md:px-12 pt-12 pb-24">
        <Link
          to="/"
          data-testid="back-to-home"
          className="inline-flex items-center gap-2 font-mono-plex text-[10px] font-semibold text-zinc-700 hover:text-zinc-950 mb-6"
        >
          <ArrowLeft weight="bold" className="w-3.5 h-3.5" />
          BACK TO TRUESTREAM
        </Link>

        <div className="font-mono-plex text-[10px] font-semibold text-zinc-500 mb-3">
          LEGAL · LAST UPDATED {LAST_UPDATED.toUpperCase()}
        </div>
        <h1 className="font-display text-5xl md:text-6xl leading-[0.95] tracking-tight text-zinc-950 mb-8">
          Privacy Policy
        </h1>

        <div className="prose prose-zinc max-w-none space-y-6 text-[15px] leading-relaxed text-zinc-800">
          <p>
            TrueStream is a fact-checking tool for social-media videos. This Privacy Policy explains what
            information we collect when you use the web app, our Android overlay bubble, or the shared
            fact-check pages — and what we do with it.
          </p>

          <h2 className="font-display text-2xl text-zinc-950 !mb-2">What we collect</h2>
          <p>
            <strong>Video URLs you submit.</strong> When you paste or share a URL, we send it to our
            backend to fetch the video&apos;s captions and run our multi-agent verification pipeline. We
            cache the resulting verdict, tied to a hash of the URL, so we can serve subsequent requests
            instantly.
          </p>
          <p>
            <strong>Account data (only if you sign in).</strong> Sign-in is via Google (Emergent-managed
            OAuth). If you sign in we store your email, display name, profile picture URL, and a session
            token. We never store your Google password — Google handles authentication.
          </p>
          <p>
            <strong>Anonymous usage counters.</strong> Anonymous browsers get up to three free fact-checks
            per session, tracked in your browser&apos;s <code>localStorage</code> only. Clearing your site
            data resets the counter.
          </p>
          <p>
            <strong>Server logs.</strong> Our backend receives your IP address in HTTP request headers so
            it can enforce rate-limits (30 requests / minute per IP) and diagnose abuse. IPs are not linked
            to your account.
          </p>
          <p>
            <strong>Android app permissions.</strong> The TrueStream Android app uses an Accessibility
            Service <em>restricted at the OS level</em> to a hard-coded list of video / audio apps
            (YouTube, TikTok, Instagram, X, Facebook, Spotify, YouTube Music, Netflix). It reads the
            currently-visible video title or URL from those apps only, and uses it to fact-check the video.
            It cannot read from any other app on your device.
          </p>

          <h2 className="font-display text-2xl text-zinc-950 !mb-2">What we do NOT collect</h2>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Your Google password (Google auth handles this).</li>
            <li>Browsing history or content from any app outside the video-app allow-list on Android.</li>
            <li>Payment information — TrueStream is currently free.</li>
            <li>Precise device location.</li>
            <li>Sensitive categories (health data, sexual orientation, religion, etc.).</li>
          </ul>

          <h2 className="font-display text-2xl text-zinc-950 !mb-2">How we use your data</h2>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>To run the fact-check pipeline you asked for.</li>
            <li>To keep a shared cache of verdicts so repeated checks are instant (video URL hash + verdict, no user identifier).</li>
            <li>To rate-limit abuse.</li>
            <li>To let signed-in users skip the anonymous 3-checks-per-browser cap.</li>
          </ul>

          <h2 className="font-display text-2xl text-zinc-950 !mb-2">Third-party services</h2>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Google Fact Check Tools API</strong> — used to look up IFCN-verified fact-checker reviews for each claim. Only the claim text is sent, never your identity.</li>
            <li><strong>OpenAI (GPT-5.2 &amp; Whisper) via Emergent</strong> — used for claim detection, evidence synthesis, and (fallback) audio transcription. Only the relevant transcript / claim text is sent.</li>
            <li><strong>Wikipedia REST API</strong> — used for background evidence lookups.</li>
            <li><strong>yt-dlp</strong> — used server-side to fetch public video captions.</li>
            <li><strong>Google OAuth</strong> — handles sign-in.</li>
          </ul>

          <h2 className="font-display text-2xl text-zinc-950 !mb-2">Retention</h2>
          <p>
            Cached verdicts follow a variable time-to-live (12&nbsp;h for breaking news, 72&nbsp;h for
            scientific claims, up to 30 days for historical claims), after which MongoDB deletes them
            automatically. User sessions expire after seven days. Signed-in user records persist until you
            request deletion (see below).
          </p>

          <h2 className="font-display text-2xl text-zinc-950 !mb-2">Your rights</h2>
          <p>
            You can request access to, correction of, or deletion of your account data at any time by
            emailing us. If you&apos;re in the EU / UK / California, you have additional rights under
            GDPR / CCPA — same email works. We reply within 30 days.
          </p>

          <h2 className="font-display text-2xl text-zinc-950 !mb-2">Children</h2>
          <p>TrueStream is not directed at children under 13, and we do not knowingly collect data from them.</p>

          <h2 className="font-display text-2xl text-zinc-950 !mb-2">Changes to this policy</h2>
          <p>
            We&apos;ll update the &ldquo;last updated&rdquo; date at the top when we make material changes.
            Continuing to use TrueStream after changes means you accept them.
          </p>

          <h2 className="font-display text-2xl text-zinc-950 !mb-2">Contact</h2>
          <p>
            Questions or requests: <a className="underline" href="mailto:hello@truestream.app">hello@truestream.app</a>.
          </p>
        </div>
      </section>
    </div>
  );
}
